import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const QuizDetailsEditor = ({
  title,
  description,
  onSave,
  className = '',
}: {
  title: string;
  description: string;
  onSave: (details: { title: string; description: string }) => void;
  className?: string;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(title);
  const [editDescription, setEditDescription] = useState(description);

  useEffect(() => {
    setEditTitle(title);
    setEditDescription(description);
  }, [title, description]);

  const handleSave = () => {
    if (editTitle.trim() === '') return;
    onSave({
      title: editTitle,
      description: editDescription,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(title);
    setEditDescription(description);
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">{title}</h1>
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            className="ml-4"
          >
            Edit Details
          </Button>
        </div>
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
          {description || ''}
        </p>
      </div>
    );
  }

  return (
    <Card className={`p-4 space-y-4 ${className}`}>
      <CardHeader className="p-0">
        <h2 className="text-lg font-semibold">Edit Quiz Details</h2>
      </CardHeader>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Title</label>
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Enter quiz title"
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Add a description for this quiz"
            className="min-h-[100px]"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={editTitle.trim() === ''}>
          Save Changes
        </Button>
      </div>
    </Card>
  );
};

export default QuizDetailsEditor;
